import { useMemo, useState } from 'react'
import { toReportRows } from './coverageReport'
import { getDetailRecords } from './detailedOutStore'
import ExportBar from './ExportBar'
import {
  buildOvertimeRows,
  filterOvertime,
  totalHours,
  totalsBy,
  type OvertimeFilters,
  type OvertimeRow,
  type OvertimeTotal,
} from './overtimeFigures'
import { OVERTIME_GROUP_LABEL, overtimeEntriesTable, overtimeTotalsTable, type OvertimeView } from './overtimeReportExport'
import { getOvertimeRecords } from './overtimeStore'
import ReportControls, { type ReportFilter } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import { fullDate } from './reportDates'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import { supervisorLabel } from './supervisorLabel'
import { useCoverageData } from './useCoverageData'
import { useLoaded } from './useLoaded'
import { useReportRange } from './useReportRange'

/**
 * docs/product/reports.md's overtime planning report: planned hours beyond
 * the normal schedule, by employee, date, shift, and assignment, with the
 * reason and the coverage gap the hours address, and totals by employee,
 * team, and pay period. Planning information.
 */
const hours = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1))

const ENTRY_COLUMNS: ReportTableColumn<OvertimeRow>[] = [
  { id: 'employee', label: 'Employee', cell: (row) => row.employee },
  { id: 'team', label: 'Team', cell: (row) => row.team },
  { id: 'date', label: 'Date', cell: (row) => fullDate(row.date) },
  { id: 'shift', label: 'Shift', cell: (row) => row.shift },
  { id: 'assignment', label: 'Assignment', cell: (row) => row.assignment },
  { id: 'hours', label: 'Planned hours', numeric: true, cell: (row) => hours(row.hours) },
  { id: 'reason', label: 'Reason', cell: (row) => row.reason ?? 'None given' },
  {
    id: 'gap',
    label: 'Coverage gap',
    cell: (row) => <span className={(row.shortBy ?? 0) > 0 ? 'font-semibold text-error' : ''}>{row.gap}</span>,
  },
  { id: 'detailedOut', label: 'Detailed out', cell: (row) => (row.detailedOut ? 'Yes' : 'No') },
]

const ENTRY_SORT: Record<string, (row: OvertimeRow) => SortValue> = {
  employee: (row) => row.employee,
  team: (row) => row.team,
  date: (row) => row.date,
  shift: (row) => row.shift,
  assignment: (row) => row.assignment,
  hours: (row) => row.hours,
  reason: (row) => row.reason,
  gap: (row) => row.shortBy,
  detailedOut: (row) => (row.detailedOut ? 1 : 0),
}

const TOTAL_SORT: Record<string, (row: OvertimeTotal) => SortValue> = {
  label: (row) => row.label,
  entries: (row) => row.entries,
  hours: (row) => row.hours,
  gapHours: (row) => row.gapHours,
}

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">{value}</p>
    </div>
  )
}

export default function OvertimeReport() {
  const range = useReportRange()
  const { from, to } = range
  const [filters, setFilters] = useState<OvertimeFilters>({ includeDetailedOut: true })
  const [view, setView] = useState<OvertimeView>('entries')
  const [sort, setSort] = useState<ReportSort>({ column: 'date', direction: 'asc' })
  const coverage = useCoverageData(from, to)
  const overtime = useLoaded(
    async () => {
      if (range.invalid) return { records: [], details: [] }
      const [records, details] = await Promise.all([getOvertimeRecords(from, to), getDetailRecords()])
      return { records, details }
    },
    { records: [], details: [] },
    [from, to],
  )
  const { lookups } = coverage
  const loading = overtime.loading || coverage.loading
  const failed = overtime.failed || coverage.failed

  const coverageRows = useMemo(() => toReportRows(coverage.rows, lookups), [coverage.rows, lookups])
  const allRows = useMemo(
    () => buildOvertimeRows(overtime.data.records, coverageRows, overtime.data.details, lookups.supervisors),
    [overtime.data, coverageRows, lookups],
  )
  const filtered = useMemo(() => filterOvertime(allRows, filters), [allRows, filters])
  const total = totalHours(filtered)
  const entryColumn = ENTRY_SORT[sort.column] ? sort.column : 'date'
  const entries = useMemo(() => sortRows(filtered, ENTRY_SORT[entryColumn], sort.direction), [filtered, entryColumn, sort.direction])
  const grouped = useMemo(
    () => (view === 'entries' ? [] : totalsBy(filtered, view)),
    [filtered, view],
  )
  const totalColumn = TOTAL_SORT[sort.column] ? sort.column : 'label'
  const groups = useMemo(
    () => (view === 'entries' || view === 'period' ? grouped : sortRows(grouped, TOTAL_SORT[totalColumn], sort.direction)),
    [grouped, view, totalColumn, sort.direction],
  )

  function chooseView(next: OvertimeView) {
    setView(next)
    setSort(next === 'entries' ? { column: 'date', direction: 'asc' } : { column: 'label', direction: 'asc' })
  }

  const set = (key: keyof OvertimeFilters) => (value: string) => setFilters((current) => ({ ...current, [key]: value || undefined }))
  const reportFilters: ReportFilter[] = [
    {
      id: 'team',
      label: 'Team',
      allLabel: 'All teams',
      value: filters.teamId ?? '',
      options: Array.from(new Set(allRows.map((row) => row.teamId))).map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown team' })),
      onChange: set('teamId'),
    },
    {
      id: 'supervisor',
      label: 'Supervisor',
      allLabel: 'All supervisors',
      value: filters.supervisorId ?? '',
      options: Array.from(new Set(allRows.flatMap((row) => (row.supervisorId ? [row.supervisorId] : [])))).map((id) => ({
        value: id,
        label: supervisorLabel(id, lookups),
      })),
      onChange: set('supervisorId'),
    },
    {
      id: 'shift',
      label: 'Shift',
      allLabel: 'All shifts',
      value: filters.shiftName ?? '',
      options: Array.from(new Set(allRows.map((row) => row.shift))).sort().map((name) => ({ value: name, label: name })),
      onChange: set('shiftName'),
    },
    {
      id: 'assignment',
      label: 'Assignment',
      allLabel: 'All assignments',
      value: filters.assignmentName ?? '',
      options: Array.from(new Set(allRows.map((row) => row.assignment))).sort().map((name) => ({ value: name, label: name })),
      onChange: set('assignmentName'),
    },
  ]

  const groupColumns: ReportTableColumn<OvertimeTotal>[] =
    view === 'entries'
      ? []
      : [
          { id: 'label', label: OVERTIME_GROUP_LABEL[view], cell: (row) => row.label },
          { id: 'entries', label: 'Entries', numeric: true, cell: (row) => row.entries },
          { id: 'hours', label: 'Planned hours', numeric: true, cell: (row) => hours(row.hours) },
          { id: 'gapHours', label: 'Hours against a coverage gap', numeric: true, cell: (row) => hours(row.gapHours) },
        ]

  const exportInput = { view, from, to, filters, lookups, generatedAt: new Date() }
  const tableCount = view === 'entries' ? entries.length : groups.length
  const empty = 'No planned overtime in these dates. Choose another pay period or clear a filter.'
  const onSort = (column: string) => setSort((current) => nextSort(current, column))

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Planned hours" value={hours(total)} />
        <Tile label="Entries" value={filtered.length} />
        <Tile label="Employees" value={new Set(filtered.map((row) => row.employeeId)).size} />
        <Tile label="Hours against a coverage gap" value={hours(filtered.filter((row) => (row.shortBy ?? 0) > 0).reduce((sum, row) => sum + row.hours, 0))} />
      </section>

      <ReportControls
        range={range}
        filters={reportFilters}
        search={{ value: filters.search ?? '', onChange: set('search') }}
        toggles={[
          {
            id: 'detailed-out',
            label: 'Include detailed-out personnel',
            checked: filters.includeDetailedOut,
            onChange: (checked) => setFilters((current) => ({ ...current, includeDetailedOut: checked })),
            activeLabel: filters.includeDetailedOut ? null : 'Detailed-out personnel excluded',
          },
        ]}
        onReset={() => setFilters({ includeDetailedOut: true })}
      />

      <div role="group" aria-label="Report view" className="flex flex-wrap">
        {(['entries', 'employee', 'team', 'period'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={view === option}
            onClick={() => chooseView(option)}
            className={`min-h-11 border border-line-strong px-4 text-small font-bold first:rounded-l-control last:rounded-r-control ${
              view === option ? 'border-primary bg-selected' : 'hover:bg-surface-2'
            }`}
          >
            {option === 'entries' ? 'Entries' : `By ${OVERTIME_GROUP_LABEL[option].toLowerCase()}`}
          </button>
        ))}
      </div>

      <ExportBar
        count={`${tableCount} ${tableCount === 1 ? 'row' : 'rows'}`}
        loading={loading}
        disabled={loading || failed || range.invalid || tableCount === 0}
        fileStem={`muster-overtime-${view}-${from}-to-${to}`}
        table={() =>
          view === 'entries' ? overtimeEntriesTable(entries, total, exportInput) : overtimeTotalsTable(groups, view, exportInput)
        }
      />

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The overtime report could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : view === 'entries' ? (
        <ReportTable
          label="Overtime planning"
          caption="Planned overtime by employee, date, shift, and assignment"
          columns={ENTRY_COLUMNS}
          rows={entries}
          rowKey={(row) => row.id}
          sort={{ ...sort, column: entryColumn }}
          onSort={onSort}
          loading={loading}
          empty={empty}
          minWidth={1200}
        />
      ) : (
        <ReportTable
          label="Overtime planning"
          caption={`Planned overtime totals by ${OVERTIME_GROUP_LABEL[view].toLowerCase()}`}
          columns={groupColumns}
          rows={groups}
          rowKey={(row) => row.key}
          sort={{ ...sort, column: totalColumn }}
          onSort={onSort}
          loading={loading}
          empty={empty}
          minWidth={700}
        />
      )}
    </div>
  )
}
